import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ImageOff, ShieldCheck, Truck, BadgeCheck, Heart } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import QuantityStepper from '../components/QuantityStepper';
import ProductCard from '../components/ProductCard';
import { ProductGridSkeleton } from '../components/Skeleton';
import SectionHeading from '../components/SectionHeading';
import usePageMetadata from '../hooks/usePageMetadata';
import * as catalogApi from '../api/endpoints/catalog';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { FAVOURITE_AUTH_REQUIRED, useFavourites } from '../context/FavouritesContext';
import { btnLg, btnPrimary, btnSecondary, containerProduct, focusRing, pageHeading, pageSection } from '../styles/ui';

const ProductDetailPage = () => {
  const { idOrSku } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [related, setRelated] = useState({ status: 'idle', products: [] });
  const { addItem, setQuantity, quantityFor } = useCart();
  const { isFavourite, toggleFavourite } = useFavourites();
  const { showToast } = useToast();

  const product = state.data;
  const favourited = product ? isFavourite(product._id) : false;

  const handleToggleFavourite = async () => {
    if (!product) return;
    try {
      const wasFavourited = favourited;
      const result = await toggleFavourite(product._id, product);
      if (result === FAVOURITE_AUTH_REQUIRED) {
        const redirect = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
        return;
      }
      showToast(wasFavourited ? 'Removed from favourites' : 'Added to favourites');
    } catch {
      // Best-effort — FavouritesContext already rolls back the optimistic
      // update on failure, nothing further to surface here.
    }
  };

  const handleAddToCart = () => {
    addItem(product);
    showToast(`${product.displayName} added to cart`);
  };

  const handleBuyNow = () => {
    addItem(product);
    navigate('/cart');
  };

  usePageMetadata({
    title: `${product?.displayName || 'Product'} | Fresh & Homely`,
    description: product?.shortDescription || 'Shop fresh products at Fresh & Homely.',
    canonicalPath: `/products/${idOrSku}`,
  });

  const load = () => {
    setState({ status: 'loading', data: null, error: null });
    setRelated({ status: 'idle', products: [] });
    catalogApi
      .getProduct(idOrSku)
      .then((response) => setState({ status: 'success', data: response.data, error: null }))
      .catch((err) => setState({ status: 'error', data: null, error: err.message }));
  };

  useEffect(load, [idOrSku]);

  // Related products are simply other items in the same category — there is
  // no dedicated "related products" concept in the backend, so this reuses
  // the existing category listing endpoint rather than inventing new data.
  useEffect(() => {
    if (!product?.category) {
      setRelated({ status: 'idle', products: [] });
      return undefined;
    }
    let active = true;
    setRelated({ status: 'loading', products: [] });
    catalogApi
      .listProducts({ category: product.category, limit: 8 })
      .then((response) => {
        if (!active) return;
        const items = (response.data || []).filter((item) => item._id !== product._id).slice(0, 4);
        setRelated({ status: 'success', products: items });
      })
      .catch(() => {
        if (active) setRelated({ status: 'error', products: [] });
      });
    return () => {
      active = false;
    };
  }, [product?.category, product?._id]);

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={containerProduct}>
          {state.status === 'loading' && <LoadingState label="Loading product..." />}
          {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}
          {state.status === 'success' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-emerald-50 rounded-4xl overflow-hidden flex items-center justify-center shadow-lifted relative">
                {product.thumbnail ? (
                  <img src={product.thumbnail} alt={product.displayName} className="w-full h-full object-cover" />
                ) : (
                  <ImageOff className="text-emerald-300" size={48} aria-hidden="true" />
                )}
                <button
                  type="button"
                  onClick={handleToggleFavourite}
                  aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
                  aria-pressed={favourited}
                  className={`absolute top-4 right-4 w-11 h-11 rounded-full bg-white/90 backdrop-blur-sm shadow-card flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 ${focusRing}`}
                >
                  <Heart
                    size={20}
                    className={favourited ? 'text-accent-500 fill-accent-500' : 'text-gray-400'}
                    aria-hidden="true"
                  />
                </button>
              </div>

              <div>
                <p className="text-emerald-700 font-medium mb-2">{product.categoryLabel}</p>
                <h1 className={`${pageHeading} mb-4`}>{product.displayName}</h1>

                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-3xl font-bold text-emerald-950">₹{product.price ?? '—'}</span>
                  {product.mrp != null && product.mrp > product.price && (
                    <span className="text-lg text-gray-400 line-through">₹{product.mrp}</span>
                  )}
                  {product.discountPercentage > 0 && (
                    <span className="bg-highlight-50 text-highlight-600 text-sm font-semibold px-2.5 py-1 rounded-full">
                      {product.discountPercentage}% OFF
                    </span>
                  )}
                </div>
                {product.sellingUom && (
                  <span className="inline-block mb-6 text-sm font-semibold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full">
                    {product.sellingUom}
                  </span>
                )}

                {product.catchWeight && (
                  <p className="text-amber-700 bg-amber-50 rounded-xl px-4 py-2.5 text-sm mb-6">
                    This item's weight may vary slightly. Catch-weight items are payable on delivery only.
                  </p>
                )}

                {product.longDescription && <p className="text-ink-secondary mb-6 leading-relaxed">{product.longDescription}</p>}

                {product.highlights?.length > 0 && (
                  <ul className="mb-8 space-y-1.5">
                    {product.highlights.map((highlight) => (
                      <li key={highlight} className="text-ink-secondary flex items-start gap-2">
                        <span className="text-emerald-600 mt-1">•</span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mb-8 flex flex-wrap items-center gap-3">
                  {product.isInStock ? (
                    quantityFor(product._id) > 0 ? (
                      <QuantityStepper
                        size="lg"
                        quantity={quantityFor(product._id)}
                        onChange={(qty) => setQuantity(product._id, qty)}
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={handleAddToCart}
                          className={`${btnSecondary} ${btnLg}`}
                        >
                          Add to Cart
                        </button>
                        <button
                          type="button"
                          onClick={handleBuyNow}
                          className={`${btnPrimary} ${btnLg}`}
                        >
                          Buy Now
                        </button>
                      </>
                    )
                  ) : (
                    <span className="inline-block bg-gray-100 text-gray-500 px-6 py-3.5 rounded-full font-semibold text-base">
                      Out of stock
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3 pt-6 border-t border-emerald-50">
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <ShieldCheck className="text-emerald-700" size={18} aria-hidden="true" />
                    </div>
                    <p className="text-xs font-medium text-ink-secondary leading-tight">Hygienically Packed</p>
                  </div>
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <Truck className="text-emerald-700" size={18} aria-hidden="true" />
                    </div>
                    <p className="text-xs font-medium text-ink-secondary leading-tight">Fast Delivery</p>
                  </div>
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <BadgeCheck className="text-emerald-700" size={18} aria-hidden="true" />
                    </div>
                    <p className="text-xs font-medium text-ink-secondary leading-tight">Quality Checked</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {state.status === 'success' &&
            (related.status === 'loading' || (related.status === 'success' && related.products.length > 0)) && (
              <div className="mt-16 pt-12 border-t border-emerald-50">
                <SectionHeading align="left" title="Related Products" description="More from the same category." />
                {related.status === 'loading' && <ProductGridSkeleton count={4} />}
                {related.status === 'success' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                    {related.products.map((item) => (
                      <ProductCard
                        key={item._id}
                        product={item}
                        quantity={quantityFor(item._id)}
                        onAddToCart={addItem}
                        onSetQuantity={setQuantity}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default ProductDetailPage;
